import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { 
  FaBookmark, FaRegBookmark, FaPhoneAlt, FaComments, 
  FaShieldAlt, FaMapMarkerAlt, FaWhatsapp, FaBan, FaFlag,
  FaCopy // Added FaCopy
} from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import * as api from "../../api/index";

const Viewshop = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [error, setError] = useState("");
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [userId, setUserId] = useState(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.id || decoded._id);
      } catch (err) {
        console.error("Auth decode error");
      }
    }
  }, []);

  const fetchShopData = useCallback(async (currentPage) => {
    try {
      setLoading(true);
      setError("");
      const res = await api.Viewshop(id, currentPage, limit, userId); 
      
      if (res?.data) {
        setShop(res.data);
        setTotalPages(res.data.pagination?.totalPages || 1);
        setIsBookmarked(!!res.data.isBookmarked);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError("You have blocked this seller and cannot view their profile.");
        setTimeout(() => navigate(-1), 3000);
      } else {
        setError("Failed to load shop details.");
      }
    } finally {
      setLoading(false);
    }
  }, [id, userId, navigate]);

  useEffect(() => {
    if (id) fetchShopData(page);
  }, [id, page, userId, fetchShopData]);

  // NEW: Handle Copy Shop Link for Web
  const handleCopyShopLink = () => {
    const shopUrl = `https://ourasap.com/shop/${id}`;
    navigator.clipboard.writeText(shopUrl)
      .then(() => alert("Shop link copied! Ready to share."))
      .catch(() => alert("Failed to copy link."));
  };

  const handleBlockSeller = async () => {
    if (!userId) return navigate("/auth");
    
    const confirmBlock = window.confirm("Are you sure you want to block this seller? You will no longer see their shop or products.");
    if (!confirmBlock) return;

    try {
      const res = await api.toggleBlockSeller({ userId, sellerId: id });
      if (res?.data?.success) {
        alert("Seller has been blocked.");
        navigate("/"); 
      }
    } catch (err) {
      alert("Failed to block seller.");
    }
  };

  const handleReportShop = () => {
    if (!userId) return navigate("/auth");

    const confirmReport = window.confirm("Are you sure you want to report this shop for a policy violation?");
    if (confirmReport) {
      alert("Thank you. We have received your report and our moderators will review this shop within 24 hours.");
    }
  };

  const handleToggleBookmark = async () => {
    if (!userId) return navigate("/auth");
    setIsBookmarked(prev => !prev);
    try {
      const res = await api.toggleBookmarkUser({ userId, sellerId: id });
      if (!res?.data?.success) setIsBookmarked(prev => !prev);
    } catch (err) {
      setIsBookmarked(prev => !prev);
    }
  };

  const handleStartConversation = async () => {
    if (!userId) return navigate("/auth");
    if (userId === id) return alert("You cannot message your own shop.");
    
    setConversationLoading(true);
    try {
      const res = await api.createConversation({ senderId: userId, receiverId: id });
      if (res?.data?.success) {
        navigate(`/chat/${res.data.conversation._id}`, {
          state: { seller: res.data.seller },
        });
      }
    } catch (err) {
      alert("Something went wrong.");
    } finally {
      setConversationLoading(false);
    }
  };

  const handleWhatsApp = () => {
    if (shop?.phone) {
      const cleanNumber = shop.phone.replace(/\D/g, "");
      const finalNumber = cleanNumber.startsWith('0') ? `234${cleanNumber.slice(1)}` : cleanNumber;
      window.open(`https://wa.me/${finalNumber}?text=${encodeURIComponent(`Hello ${shop.Name}, I saw your shop on OurASAP...`)}`, "_blank");
    }
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen text-teal-600 font-bold">Loading...</div>;
  if (error) return <div className="text-center mt-20 text-red-500 font-bold px-4">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full transition"><IoMdArrowBack size={24} /></button>
        <h1 className="text-sm font-bold truncate max-w-[200px]">{shop.Name}</h1>
        
        {/* Actions Row: Copy & Bookmark */}
        <div className="flex items-center gap-2">
          <button onClick={handleCopyShopLink} className="p-2 hover:bg-gray-100 rounded-full transition text-gray-500">
            <FaCopy size={20} />
          </button>
          <button onClick={handleToggleBookmark} className="p-2">
            {isBookmarked ? <FaBookmark size={22} className="text-orange-600" /> : <FaRegBookmark size={22} className="text-gray-400" />}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 mt-4 space-y-6">
        {/* Profile Card */}
        <div className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100">
          <div className="h-32 bg-teal-600"></div>
          <div className="px-6 pb-6 relative">
            <img src={shop.profileImage} className="w-28 h-28 rounded-2xl border-4 border-white shadow-md -mt-12 bg-white object-cover" alt="Shop" />
            <div className="mt-4">
              <h2 className="text-2xl font-black">{shop.Name}</h2>
              <div className="flex items-center text-gray-500 text-sm mt-1 gap-1"><FaMapMarkerAlt size={12} /> {shop.lga}</div>
              <p className="mt-3 text-gray-600 leading-relaxed">{shop.bio}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">
              <h3 className="text-lg font-bold mb-6">Gallery</h3>
              <div className="grid grid-cols-3 gap-4">
                {shop.shopImages?.map((img, i) => (
                  <img key={i} src={img} onClick={() => setSelectedImage(img)} className="aspect-square object-cover rounded-xl cursor-pointer hover:opacity-90" alt="Gallery" />
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">
              <h3 className="text-lg font-bold mb-4">Contact Business</h3>
              <div className="space-y-3">
                {/* onClick={() => userId ? (window.location.href=`tel:${shop.phone}`) : navigate("/auth")} */}
              {userId !== id && (
                <button  onClick={() => userId ? (window.location.href=`tel:${shop.phone}`) : navigate("/auth")}  className="w-full py-3 border-2 border-teal-600 text-teal-700 font-bold rounded-2xl flex items-center justify-center gap-2">
                  <FaPhoneAlt /> { userId ? shop.phone || "Call" : "View Number"  }
                </button>
                )}
                {userId !== id && (
                <button   onClick={() => userId ? handleWhatsApp : navigate("/auth")}  className="w-full py-3 border-2 border-green-500 text-green-600 font-bold rounded-2xl flex items-center justify-center gap-2">
                  <FaWhatsapp size={20} /> WhatsApp
                </button>
                )}
                {userId !== id && (
                  <button onClick={handleStartConversation} disabled={conversationLoading} className="w-full py-3 bg-teal-600 text-white font-bold rounded-2xl">
                    {conversationLoading ? "..." : "Message"}
                  </button>
                )}
                {userId !== id && (
                  <button 
                    onClick={() => navigate("/create-order", { state: { sellerId: id } })} 
                    className="w-full bg-orange-600 text-white font-bold py-4 rounded-2xl flex flex-col items-center hover:bg-orange-700 transition shadow-lg"
                  >
                    <span>Pay with OurASAP</span>
                    <span className="text-[10px] uppercase opacity-80 tracking-widest">Escrow Protected</span>
                  </button>
                )}
              </div>

              {/* Moderation Actions */}
              {userId && userId !== id && (
                <div className="mt-8 pt-6 border-t flex gap-2">
                  <button onClick={handleReportShop} className="flex-1 py-2 border rounded-xl text-gray-500 text-xs font-bold flex items-center justify-center gap-1"><FaFlag /> Report</button>
                  <button onClick={handleBlockSeller} className="flex-1 py-2 border border-red-100 rounded-xl text-red-600 text-xs font-bold flex items-center justify-center gap-1"><FaBan /> Block</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedImage && (
        <div onClick={() => setSelectedImage(null)} className="fixed inset-0 bg-black/90 flex justify-center items-center z-50 p-4">
          <img src={selectedImage} className="max-w-full max-h-full object-contain" alt="Preview" />
        </div>
      )}
    </div>
  );
};

export default Viewshop;